import React, { useEffect, useRef, useState } from 'react';
import { bool, func, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import {
  getListingPackages,
  isShowablePackage,
  toEnquiryPackage,
} from '../../util/listingPackages';
import { renderRichText } from '../../util/richTextRender';
import { PackagePrice } from '../../components';
import ContactButton from './ContactButton';

import css from './SectionPackages.css';

// See more and Show less slide the description's height. The clamp itself
// can't be transitioned in CSS, so this uses the Web Animations API, and
// skips it for readers who prefer reduced motion or where it is missing.
// Ease-out (quart): it moves at once and settles gently. Longer slides take
// a little longer, up to a cap.
const SLIDE_EASING = 'cubic-bezier(0.25, 1, 0.5, 1)';
const slideDuration = distance => Math.min(280, 160 + Math.abs(distance) / 8);

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Slides el from one height to another, then calls onFinish. Returns the
// running animation, or null after calling onFinish straight away.
const slideHeight = (el, from, to, onFinish) => {
  if (typeof el.animate !== 'function' || prefersReducedMotion() || from === to) {
    onFinish();
    return null;
  }
  el.style.overflow = 'hidden';
  const animation = el.animate([{ height: `${from}px` }, { height: `${to}px` }], {
    duration: slideDuration(to - from),
    easing: SLIDE_EASING,
    fill: 'forwards',
  });
  animation.onfinish = () => {
    onFinish();
    el.style.overflow = '';
    animation.cancel();
  };
  return animation;
};

// The description's height with or without the five-line clamp, read by
// switching the clamp class for one measurement. A clamped box's scrollHeight
// can't stand in for the full height: Chrome undercounts it by up to several
// lines, and the slide then stopped short and jumped.
const heightWhen = (el, clamped) => {
  const wasClamped = el.classList.contains(css.descriptionClamped);
  el.classList.toggle(css.descriptionClamped, clamped);
  const { height } = el.getBoundingClientRect();
  el.classList.toggle(css.descriptionClamped, wasClamped);
  return height;
};

// A package's title and description. Descriptions can be 5000 characters:
// the first five lines show until the reader asks for the rest. See more only
// appears when the text is cut, which depends on the column width, so it is
// measured again on resize. Show less cuts it again.
const PackageDetails = props => {
  const { title, description } = props;
  // `open` is what the reader asked for (the button's label); `expanded` is
  // the text without the clamp. They differ while Show less slides: the
  // label turns at once, the clamp comes in at the end.
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const detailsRef = useRef(null);
  const descriptionRef = useRef(null);
  const slideRef = useRef(null);

  // A slide still running when the package unmounts must not call back
  useEffect(() => () => slideRef.current && slideRef.current.cancel(), []);

  useEffect(() => {
    if (expanded) {
      return undefined;
    }
    const measure = () => {
      const el = descriptionRef.current;
      if (el) {
        setIsClamped(el.scrollHeight > el.clientHeight + 1);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [description, expanded]);

  const handleToggle = () => {
    const descriptionEl = descriptionRef.current;
    if (slideRef.current || !descriptionEl) {
      return;
    }
    const slideDone = () => {
      slideRef.current = null;
    };

    const currentHeight = descriptionEl.getBoundingClientRect().height;
    if (!expanded) {
      // The clamp comes off when this renders; slide from the cut height to
      // the full text.
      const fullHeight = heightWhen(descriptionEl, false);
      setOpen(true);
      setExpanded(true);
      slideRef.current = slideHeight(descriptionEl, currentHeight, fullHeight, slideDone);
      return;
    }

    // Show less keeps the package's top where it is. A reader scrolled past
    // it (or under the sticky topbar) would land below the cut text, so take
    // them back to the title.
    const el = detailsRef.current;
    const { scrollMarginTop } = window.getComputedStyle(el);
    if (el.getBoundingClientRect().top < (parseFloat(scrollMarginTop) || 0)) {
      el.scrollIntoView({ block: 'start' });
    }
    // The text stays whole while it shrinks; the clamp and its ellipsis come
    // in at the end.
    setOpen(false);
    slideRef.current = slideHeight(
      descriptionEl,
      currentHeight,
      heightWhen(descriptionEl, true),
      () => {
        slideDone();
        setExpanded(false);
      }
    );
  };

  return (
    <div ref={detailsRef} className={css.details}>
      <h3 className={css.packageTitle}>{title}</h3>
      {description ? (
        <div
          ref={descriptionRef}
          className={classNames(css.description, { [css.descriptionClamped]: !expanded })}
        >
          {renderRichText(description)}
        </div>
      ) : null}
      {isClamped || expanded ? (
        <button type="button" className={css.toggle} aria-expanded={open} onClick={handleToggle}>
          <FormattedMessage id={open ? 'SectionPackages.showLess' : 'SectionPackages.seeMore'} />
        </button>
      ) : null}
    </div>
  );
};

PackageDetails.defaultProps = {
  description: null,
};

PackageDetails.propTypes = {
  title: string.isRequired,
  description: string,
};

// Figma 314:1746: "Packages" heading and intro over one "Service page / Package"
// row (314:1769) per package. Old listings come through getListingPackages too,
// so up to 5 rows can show.
const SectionPackages = props => {
  const { listing, showContactUser, onContactUser } = props;
  const packages = getListingPackages(listing).filter(isShowablePackage);

  if (packages.length === 0) {
    return null;
  }

  // Very old listings have a priced package 1 without a name (Review Focus 1).
  const listingTitle = listing.attributes.title;

  return (
    <section className={css.root}>
      <div className={css.heading}>
        <h2 className={css.title}>
          <FormattedMessage id="SectionPackages.title" />
        </h2>
        <p className={css.intro}>
          <FormattedMessage id="SectionPackages.intro" />
        </p>
      </div>
      <ul className={css.packages}>
        {packages.map((pkg, index) => {
          const title =
            typeof pkg.title === 'string' && pkg.title.trim() ? pkg.title : listingTitle;
          const enquiryPackage = toEnquiryPackage({ ...pkg, title });
          return (
            <li className={css.package} key={index}>
              <PackageDetails title={title} description={pkg.description || null} />
              <div className={css.pricing}>
                <div className={css.amount}>
                  <span className={css.from}>
                    <FormattedMessage id="SectionPackages.from" />
                  </span>
                  <PackagePrice
                    className={css.price}
                    amountClassName={css.priceAmount}
                    price={enquiryPackage.price}
                    pricingModel={pkg.pricingModel}
                  />
                </div>
                {showContactUser ? (
                  <ContactButton
                    className={css.contactButton}
                    onClick={() => onContactUser(true, enquiryPackage)}
                  >
                    <FormattedMessage id="SectionPackages.contact" />
                  </ContactButton>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

SectionPackages.defaultProps = {
  showContactUser: false,
};

SectionPackages.propTypes = {
  listing: object.isRequired,
  showContactUser: bool,
  onContactUser: func.isRequired,
};

export default SectionPackages;
